import { Alert as AntAlert } from 'antd';
import Link from 'antd/es/typography/Link';
import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize, IconVariant } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';
import { Checkbox } from '../Checkbox/Checkbox';
import { Tooltip } from '../Tooltip';

export type AlertVariant = 'warning' | 'success' | 'info' | 'error';

export interface AlertCheckboxItem {
  key?: string;
  label: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

export type AlertProps = {
  variant: AlertVariant;
  message: string | React.ReactNode;
  description?: string;
  showIcon?: boolean;
  closable?: boolean;
  onClose?: () => void;
  viewable?: boolean;
  viewText?: string;
  viewDisabled?: boolean;
  viewTooltip?: string;
  fullWidth?: boolean;
  onView?: () => void;
  /** Renders a stack of checkboxes below the message — Figma `Action=Checkbox`/`Checkboxes`. */
  checkboxItems?: AlertCheckboxItem[];
  className?: string;
};

const StyledAlert = styled(AntAlert)<{
  $variant: AlertVariant;
  $fullWidth: boolean;
}>`
  &.ant-alert {
    position: relative;
    display: flex;
    align-items: start;
    justify-content: space-between;
    max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
    border-radius: ${theme.dime.radius.m};
    padding: ${theme.dime.spacing.m} ${theme.dime.spacing.l};
    border: 1px solid;
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);

    /* Remove default Ant Design styling */
    background: none;

    /* Icon and content layout */
    .ant-alert-icon {
      margin-right: ${theme.dime.spacing.m};
      flex-shrink: 0;
    }

    .ant-alert-content {
      display: inline-block;
      vertical-align: top;
    }

    .ant-alert-message {
      margin-bottom: 0;
    }

    /* No margin here — the gap belongs to whichever content actually renders
       (DescriptionText or CheckboxList below), so it's never doubled up via
       margin-collapse with that content's own margin-top. */
    .ant-alert-description {
      margin-top: 0;
    }

    .ant-alert-action {
      display: flex;
      align-items: start;
      margin-left: ${theme.dime.spacing.l};
      height: 18px;
    }

    .ant-alert-close-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      margin-left: ${theme.dime.spacing.l};

      .ant-alert-close-text {
        font-size: 16px;
        line-height: 16px;
      }
    }

    /* Variant-specific styles */
    &.info {
      background-color: ${theme.dime.colors.fill.infoSecondary};
      border-color: ${theme.dime.colors.border.info};
      color: ${theme.dime.colors.text.primary};

      .ant-alert-icon {
        color: ${theme.dime.colors.icon.info};
      }

      .ant-alert-close-icon {
        color: ${theme.dime.colors.icon.primary};

        &:hover {
          color: ${theme.dime.colors.link.primary};
        }
      }
    }

    &.success {
      background-color: ${theme.dime.colors.fill.successSecondary};
      border-color: ${theme.dime.colors.border.success};
      color: ${theme.dime.colors.text.primary};

      .ant-alert-icon {
        color: ${theme.dime.colors.icon.success};
      }

      .ant-alert-close-icon {
        color: ${theme.dime.colors.icon.primary};

        &:hover {
          color: ${theme.dime.colors.link.primary};
        }
      }
    }

    &.warning {
      background-color: ${theme.dime.colors.fill.warningSecondary};
      border-color: ${theme.dime.colors.border.warning};
      color: ${theme.dime.colors.text.primary};

      .ant-alert-icon {
        color: ${theme.dime.colors.icon.warning};
      }

      .ant-alert-close-icon {
        color: ${theme.dime.colors.icon.primary};

        &:hover {
          color: ${theme.dime.colors.link.primary};
        }
      }
    }

    &.error {
      background-color: ${theme.dime.colors.fill.criticalSecondary};
      border-color: ${theme.dime.colors.border.critical};
      color: ${theme.dime.colors.text.primary};

      .ant-alert-icon {
        color: ${theme.dime.colors.icon.critical};
      }

      .ant-alert-close-icon {
        color: ${theme.dime.colors.icon.primary};

        &:hover {
          color: ${theme.dime.colors.link.primary};
        }
      }
    }
  }
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${theme.dime.spacing.m};
`;

const DescriptionText = styled(Typography)`
  margin-top: ${theme.dime.spacing.s};
`;

const StyledLink = styled(Link)<{ $disabled?: boolean }>`
  font-size: ${theme.dime.fonts.sizes.sm};
  font-family: ${theme.dime.fonts.families.roboto};
  color: ${({ $disabled }) =>
    $disabled
      ? theme.dime.colors.text.disabled
      : theme.dime.colors.link.primary} !important;
  cursor: ${({ $disabled }) =>
    $disabled ? 'not-allowed' : 'pointer'} !important;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  text-decoration: ${({ $disabled }) =>
    $disabled ? 'none' : 'auto'} !important;

  &&:hover {
    color: ${({ $disabled }) =>
      $disabled
        ? theme.dime.colors.text.disabled
        : theme.dime.colors.link.primaryHover} !important;
    text-decoration: ${({ $disabled }) =>
      $disabled ? 'none' : 'auto'} !important;
  }
`;

const variantIcons: Record<AlertVariant, IconType> = {
  info: IconType.Information,
  success: IconType.Approved,
  warning: IconType.Warning,
  error: IconType.Warning
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  message,
  description,
  showIcon = true,
  closable = false,
  onClose,
  viewable = false,
  viewText = 'View',
  viewDisabled = false,
  viewTooltip,
  fullWidth = false,
  onView,
  checkboxItems,
  className = '',
  ...props
}) => {
  const renderViewAction = () => {
    if (!viewable) return false;

    const link = (
      <StyledLink
        $disabled={viewDisabled}
        onClick={viewDisabled ? undefined : onView}
      >
        {viewText}
      </StyledLink>
    );

    if (viewDisabled && viewTooltip) {
      return (
        <Tooltip title={viewTooltip}>
          <span>{link}</span>
        </Tooltip>
      );
    }

    return link;
  };

  return (
    <StyledAlert
      $variant={variant}
      $fullWidth={fullWidth}
      className={`${variant} ${className}`}
      message={<Typography variant="body1">{message}</Typography>}
      description={
        (!!description || !!checkboxItems?.length) && (
          <>
            {!!description && (
              <DescriptionText variant="body1">{description}</DescriptionText>
            )}
            {!!checkboxItems?.length && (
              <CheckboxList>
                {checkboxItems.map((item, index) => (
                  <Checkbox
                    key={item.key ?? index}
                    label={item.label}
                    checked={item.checked}
                    disabled={item.disabled}
                    onChange={item.onChange}
                  />
                ))}
              </CheckboxList>
            )}
          </>
        )
      }
      showIcon={showIcon}
      icon={
        !!showIcon && (
          <IconWrap
            type={variantIcons[variant]}
            size={IconSize.Small}
            variant={IconVariant.Solid}
          />
        )
      }
      closable={closable}
      onClose={onClose}
      closeIcon={<IconWrap type={IconType.Close} size={IconSize.Small} />}
      type={variant}
      action={renderViewAction()}
      {...props}
    />
  );
};
